import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { makeStyles } from 'tss-react/mui';

import { CircularProgress, Collapse, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

import API from 'app/API';
import Hubspot from 'app/main/common/Hubspot';
import TextField from 'app/ui-kit/components/TextField';
import Captcha from 'utils/Captcha';

const useStyles = makeStyles()(() => ({
    root: {
        position: 'relative',
        top: -80,
        maxWidth: 980,
        margin: '0 auto',
        padding: 50,
        background: '#fff',
        boxShadow: '0px 20px 50px rgba(186, 184, 176, 0.2)',
        borderRadius: 28,
        '& .g-recaptcha > div': {
            margin: 'initial'
        },
        '@media (max-width: 720px)': {
            flexDirection: 'column'
        }
    },
    box: {
        display: 'flex',
        justifyContent: 'space-between',
        flexWrap: 'wrap'
    },
    header: {
        fontSize: 35,
        fontWeight: 700
    },
    body: {
        fontSize: 18,
        fontWeight: 400
    },
    field: {
        margin: '12px 0'
    },
    button: {
        fontSize: 16,
        fontWeight: 600,
        padding: '14px 60px',
        borderRadius: 100,
        '@media (max-width: 720px)': {
            marginTop: 20
        }
    }
}));

const recaptchaRef = React.createRef();

const PartnerCTA = ({ innerRef }) => {
    const { classes } = useStyles();
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleContactMe = () => {
        const captcha = recaptchaRef.current.getValue();
        if (!captcha) {
            return setError('Please submit captcha');
        }

        setLoading(true);
        API.fetch(API.CHECKOUT_EMAIL, {
            firstName: name,
            email,
            phone,
            captcha,
            license: 'professional',
            lastName: '',
            companyName: '',
            additionalInfo: ''
        }).then((r) => {
            setLoading(false);
            if (r.data.error) {
                return setError(r.data.error);
            }
            Hubspot.send(Hubspot.CHECKOUT, {
                firstname: name,
                lastname: '',
                email,
                phone,
                company: '',
                license_type: 'cta'
            });
            setSuccess(true);
        });
    };

    let content;
    if (success) {
        content = (
            <Box mt={2}>
                <Typography variant="body2">
                    Our team will contact you as soon as possible! <span style={{ fontSize: 32 }}>🤝</span>
                </Typography>
            </Box>
        );
    } else {
        content = (
            <Box mt={2}>
                <TextField
                    fullWidth
                    name="name"
                    label="Name"
                    defaultValue={name}
                    className={classes.field}
                    onChange={(e) => setName(e.target.value)}
                />
                <TextField
                    fullWidth
                    name="email"
                    label="Email"
                    type="email"
                    defaultValue={email}
                    className={classes.field}
                    onChange={(e) => setEmail(e.target.value)}
                />
                <TextField
                    fullWidth
                    name="phone"
                    type="phone"
                    label="Phone number"
                    defaultValue={phone}
                    className={classes.field}
                    onChange={(e) => setPhone(e.target.value)}
                />
                <Captcha id="vulners-partner-cta-captcha" ref={recaptchaRef} />
                {error && (
                    <Typography variant="body2" color="primary">
                        {error}
                    </Typography>
                )}
                <Button
                    style={{ marginTop: 12 }}
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    onClick={handleContactMe}
                    disabled={loading}
                    endIcon={loading && <CircularProgress size={24} />}
                >
                    Submit
                </Button>
            </Box>
        );
    }

    return (
        <div style={{ background: '#fafafa' }} ref={innerRef}>
            <Box className={classes.root}>
                <div className={classes.box}>
                    <Box>
                        <Typography variant="h2">Become a partner with Vulners</Typography>
                        <Typography variant="body2">To expand your security business</Typography>
                    </Box>
                    {!open && (
                        <Button
                            variant="contained"
                            color="primary"
                            className={classes.button}
                            onClick={() => setOpen(!open)}
                        >
                            {open ? 'Submit' : 'Learn More'}
                        </Button>
                    )}
                </div>
                <Collapse in={open}>{content}</Collapse>
            </Box>
        </div>
    );
};

export default observer(PartnerCTA);
